import { CommonModule } from '@angular/common';
import { Component, computed, input, InputSignal } from '@angular/core';
import { AppStyle } from 'src/app/models/app-style.type';
import {
  distinctTileProps,
  distinctTileV2Props,
  MapTile,
  Tile,
  TileGrid,
  TileType,
} from 'src/app/models/statistics';
import { ScrollFadeDirective } from 'src/app/table/directives/scroll-fade.directive';
import { CalendarChartComponent } from '../calendar-chart/calendar-chart.component';
import { CalendarChartInput } from '../calendar-chart/models/calendar-chart-input.interface';
import { GoogleMapsComponent } from '../google-maps/google-maps.component';
import {
  GoogleMapsInput,
  NON_INTERACTIVE_GOOGLE_MAPS_OPTIONS,
} from '../google-maps/model/google-maps-input.interface';
import { TileV2Component } from '../tile/components/tile-v2/tile-v2.component';
import { TileV2 } from '../tile/models/tile-v2.interface';
import { TileComponent } from '../tile/tile.component';
import { TimelineChartV2Component } from '../timeline-chart/components/timeline-chart-v2/timeline-chart-v2.component';
import { TimelineChartInputV2 } from '../timeline-chart/models/timeline-chart-input.interface';

@Component({
  selector: 'pozi-tile-grid',
  imports: [
    TileComponent,
    CommonModule,
    ScrollFadeDirective,
    CalendarChartComponent,
    GoogleMapsComponent,
    TimelineChartV2Component,
    TileV2Component,
  ],
  templateUrl: './tile-grid.component.html',
  styleUrl: './tile-grid.component.scss',
})
export class TileGridComponent {
  grid: InputSignal<TileGrid> = input.required<TileGrid>();
  appStyle = input<AppStyle>('default');

  /** Forwarded to each tile: cap the extension to the first few lines, no scroll. */
  limitExtension = input<boolean>(false);

  /** Forwarded to each tile: make the whole tile a single link (for PDF export). */
  clickableTile = input<boolean>(false);

  hasTileV2 = computed<boolean>(() =>
    Object.values(this.grid().tiles).some((t) => {
      const hasV2Props = distinctTileV2Props.some((p) => Object.hasOwn(t, p));
      const hasV1Props = distinctTileProps.some((p) => Object.hasOwn(t, p));
      return hasV2Props && !hasV1Props;
    })
  );

  readonly googleMapsInputMap = computed<Map<string, GoogleMapsInput>>(() => {
    const mapTile = new Map<string, GoogleMapsInput>();
    for (const [tileId, tile] of Object.entries(this.grid().tiles)) {
      if (this.isMapTile(tile)) {
        mapTile.set(
          tileId,
          this.getGoogleMapsInputFromMapTile(tile as MapTile)
        );
      }
    }
    return mapTile;
  });

  hasCalendarAndTimeline = computed(() => {
    return this.hasCalendarAndTimelineTile(this.grid());
  });

  nonInteractiveGoogleMapsOptions = NON_INTERACTIVE_GOOGLE_MAPS_OPTIONS;

  readonly keys = computed<string[]>(() => {
    return Object.keys(this.grid().tiles);
  });

  readonly keysWithTemplate = computed<string[]>(() => {
    return this.keys().filter((key) =>
      this.grid().template.flat().includes(key)
    );
  });

  readonly gridTemplateAreas = computed<string>(() => {
    return this.grid()
      .template.map(
        (lineArray: string[]) =>
          `'${lineArray.map((area) => (!this.keys().includes(area) ? '.' : area)).join(' ')}'`
      )
      .join('\n');
  });

  readonly gridTemplateColumns = computed<string>(() => {
    // In the report (limitExtension) use `minmax(0, 1fr)` so columns stay equal
    // and never grow to fit long extension values. Plain `1fr` is
    // `minmax(auto, 1fr)`, whose auto minimum expands the track to the value's
    // content width, pushing the grid offscreen in the generated PDF. With a 0
    // minimum the value is ellipsised instead (see tile-v2 `.extension.limited`).
    const track = this.limitExtension() ? 'minmax(0, 1fr)' : '1fr';
    return this.grid()
      .template[0].map(() => track)
      .join(' ');
  });

  readonly gridTemplateRows = computed<string>(() => {
    return this.grid()
      .template.map(() => '1fr')
      .join('');
  });

  getTile(key: string): Tile {
    return this.grid().tiles[key] as Tile;
  }

  getTileV2(key: string): TileV2 {
    return this.grid().tiles[key] as TileV2;
  }

  getCalendarChart(key: string): CalendarChartInput {
    return this.grid().tiles[key] as CalendarChartInput;
  }

  getMapTile(key: string): MapTile {
    return this.grid().tiles[key] as MapTile;
  }

  isMapTile(tile: TileType): boolean {
    return Object.hasOwn(tile, 'drawings');
  }

  getTimelineChart(key: string): TimelineChartInputV2 {
    return this.grid().tiles[key] as TimelineChartInputV2;
  }

  getGoogleMapsInputFromMapTile(mapTile: MapTile): GoogleMapsInput {
    return {
      ...mapTile,
      grayscale:
        mapTile.grayscale !== undefined
          ? mapTile.grayscale
          : !mapTile.mapTypeId || mapTile.mapTypeId === 'roadmap',
    };
  }

  /**
   * Returns true if the provided TileGrid contains at least one CalendarChartInput
   * and at least one TimelineChartInputV2 among its tiles.
   *
   * The runtime detection is structural:
   * - A tile is considered a TimelineChartInputV2 if it has a `timelines` property that is an array.
   * - A tile is considered a CalendarChartInput if it has an `items` property that's an array
   *   and the first element (if present) looks like a CalendarChartItemV2:
   *   it has a `date` (string) and a `bars` array.
   *
   * @param grid - TileGrid to inspect.
   * @returns true if both a calendar and a timeline input are present, otherwise false.
   */
  private hasCalendarAndTimelineTile(grid: TileGrid): boolean {
    if (!grid || !grid.tiles) return false;

    let foundCalendar = false;
    let foundTimeline = false;

    const values = Object.values(grid.tiles);

    for (const v of values) {
      if (!v || typeof v !== 'object') continue;

      if (!foundTimeline && this.isTimelineChartInput(v)) {
        foundTimeline = true;
      }

      if (!foundCalendar && this.isCalendarChartInput(v)) {
        foundCalendar = true;
      }

      if (foundCalendar && foundTimeline) return true;
    }

    return false;
  }

  /** Type-guard: detect TimelineChartInputV2 by presence of `timelines` or `amChart` attribute. */
  private isTimelineChartInput(obj: any): obj is TimelineChartInputV2 {
    return obj && ('amChart' in obj || 'timelines' in obj);
  }

  /**
   * Type-guard: detect CalendarChartInput by presence of `calendarChartItems` attribute. */
  private isCalendarChartInput(obj: any): obj is CalendarChartInput {
    return obj && 'calendarChartItems' in obj;
  }
}
